import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  TextField,
  InputAdornment,
  IconButton,
  Icon,
  CircularProgress,
  List,
  ListItemAvatar,
  Avatar,
  ListItemText,
  ListItemButton,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useLazyGetEstudiantesQuery } from "services/escuela/estudiantes.service";
import { useMaterialUIController } from "context";

const StudentSearch = ({ onSelectStudent }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 5;
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [triggerGetEstudiantes, { data: studentsData, isFetching, isError }] =
    useLazyGetEstudiantesQuery();

  const fetchStudents = (newOffset, query) => {
    if (!query) return;
    triggerGetEstudiantes({
      filter_params: {
        buscar: query,
        limit,
        offset: newOffset,
        query:
          "{user{first_name,apellido_materno,apellido_paterno,foto},numero_de_control,url,id,curp}",
      },
      shape: {
        id: "id",
        url: "url",
        nombre: "user.first_name",
        apellido_materno: "user.apellido_materno",
        apellido_paterno: "user.apellido_paterno",
        numero_de_control: "numero_de_control",
        foto: "user.foto",
        curp: "curp",
      },
    });
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    setOffset(0);
    fetchStudents(0, searchQuery);
  };

  const handleNextPage = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
    fetchStudents(newOffset, searchQuery);
  };

  const handlePrevPage = () => {
    const newOffset = Math.max(0, offset - limit);
    setOffset(newOffset);
    fetchStudents(newOffset, searchQuery);
  };

  const students = studentsData?.results || studentsData || [];
  const totalStudents = studentsData?.count || 0;
  const next = studentsData?.next;
  const previous = studentsData?.previous;

  return (
    <MDBox>
      <form onSubmit={handleSearch}>
        <TextField
          fullWidth
          label="Buscar estudiante (Nombre, Control, CURP...)"
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton type="submit" edge="end">
                  <Icon>search</Icon>
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </form>

      <MDBox mt={2}>
        {isFetching ? (
          <MDBox display="flex" justifyContent="center" p={3}>
            <CircularProgress color="info" />
          </MDBox>
        ) : isError ? (
          <MDTypography color="error" variant="body2" align="center">
            Error al buscar estudiantes.
          </MDTypography>
        ) : students.length > 0 ? (
          <>
            <List>
              {students.map((student) => (
                <ListItemButton
                  key={student.id}
                  onClick={() => onSelectStudent(student)}
                  sx={{
                    border: darkMode
                      ? "1px solid #344767"
                      : "1px solid #e0e0e0",
                    borderRadius: "8px",
                    mb: 1,
                    "&:hover": {
                      backgroundColor: darkMode
                        ? "rgba(255,255,255,0.05)"
                        : "#f5f5f5",
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar src={student.foto || ""} alt={student.nombre}>
                      {!student.foto && <Icon>person</Icon>}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <MDTypography
                        variant="button"
                        fontWeight="medium"
                        color={darkMode ? "white" : "dark"}
                      >
                        {`${student.nombre || ""} ${student.apellido_paterno || ""} ${student.apellido_materno || ""}`}
                      </MDTypography>
                    }
                    secondary={
                      <MDTypography variant="caption" color="text">
                        {`No. Control: ${student.numero_de_control || "-"} | CURP: ${student.curp || "-"}`}
                      </MDTypography>
                    }
                  />
                  <Icon color="action">chevron_right</Icon>
                </ListItemButton>
              ))}
            </List>

            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={2}
            >
              <MDButton
                variant="outlined"
                color="accent"
                disabled={!previous}
                onClick={handlePrevPage}
              >
                Anterior
              </MDButton>
              <MDBox>
                <MDTypography variant="body2" color="text">
                  {`${offset} - ${offset + students.length} de ${totalStudents}`}
                </MDTypography>
              </MDBox>
              <MDButton
                variant="contained"
                color="accent"
                disabled={!next}
                onClick={handleNextPage}
              >
                Siguiente
              </MDButton>
            </MDBox>
          </>
        ) : searchQuery && !isFetching ? (
          <MDTypography variant="body2" color="text" align="center">
            No se encontraron resultados.
          </MDTypography>
        ) : null}
      </MDBox>
    </MDBox>
  );
};

StudentSearch.propTypes = {
  onSelectStudent: PropTypes.func.isRequired,
};

export default StudentSearch;
